<script setup lang="ts">
import { ref, computed } from "vue";
import {
  angle_small_down,
  angle_small_up,
  edit,
  trash_can,
  plus_small,
} from "pn-design-assets/pn-assets/icons";
import type { ApiTagCategory } from "@/types/tagManagement";
import AddCategoryModal from "@/components/tagManagement/AddCategoryModal.vue";
import ConfirmationModal from "@/views/modals/ConfirmationModal.vue";
import { useTagManagementStore } from "@/stores/tagManagement";

const MAX_DEPTH = 3;

const tagManagementStore = useTagManagementStore();

const props = defineProps<{
  category: ApiTagCategory;
  themeId: number;
  depth: number;
}>();

const isExpanded = ref(false);
const showAddSubcategoryModal = ref(false);
const showDeleteModal = ref(false);
const showEditModal = ref(false);
const isDeleting = ref(false);

const openEditModal = () => {
  showEditModal.value = true;
};

const confirmDelete = async () => {
  isDeleting.value = true;
  await tagManagementStore.deleteCategory(props.category.categoryId);
  isDeleting.value = false;
  showDeleteModal.value = false;
};

const openDeleteModal = () => {
  showDeleteModal.value = true;
};

const swedishTags = computed(
  () => props.category.tags.find((t) => t.language === "swedish")?.tags ?? [],
);
const englishTags = computed(
  () => props.category.tags.find((t) => t.language === "english")?.tags ?? [],
);
const expandIcon = computed(() =>
  isExpanded.value ? angle_small_up : angle_small_down,
);
const deleteHeading = computed(
  () => `Are you sure you want to delete "${props.category.svCategoryName}"?`,
);
</script>

<template>
  <div class="category-card flex flex-direction-column">
    <div class="category-header flex align-center gap-8">
      <pn-button
        :icon="expandIcon"
        appearance="light"
        variant="borderless"
        :icon-only="true"
        small="true"
        :aria-label="`${isExpanded ? 'Collapse' : 'Expand'} ${
          category.svCategoryName
        }`"
        @click="isExpanded = !isExpanded"
      ></pn-button>
      <span class="pn-m font-bold">{{ category.svCategoryName }}</span>
      <div class="flex-spacer"></div>
      <pn-button
        :icon="edit"
        appearance="light"
        variant="borderless"
        left-icon="true"
        :aria-label="`Edit details for ${category.svCategoryName}`"
        @click="openEditModal"
      >
        Edit details
      </pn-button>
      <pn-button
        :icon="trash_can"
        appearance="light"
        variant="borderless"
        :icon-only="true"
        :aria-label="`Delete ${category.svCategoryName}`"
        @click="openDeleteModal"
      ></pn-button>
    </div>
    <div
      v-if="isExpanded"
      class="expanded-content flex flex-direction-column gap-16"
    >
      <span class="pn-xs category-english-name">
        Category name in English:
        <span class="font-bold">{{ category.enCategoryName }}</span>
      </span>
      <div
        v-if="swedishTags.length || englishTags.length"
        class="tags-wrapper flex flex-direction-column gap-8"
      >
        <span class="pn-xs font-bold">Category tags</span>
        <div class="tags-section">
          <div class="tag-group flex flex-direction-column gap-8">
            <span class="pn-xs">Swedish</span>
            <div class="flex gap-8 flex-wrap">
              <span v-for="tag in swedishTags" :key="tag" class="tag-chip">{{
                tag
              }}</span>
            </div>
          </div>
          <div class="tag-group flex flex-direction-column gap-8">
            <span class="pn-xs">English</span>
            <div class="flex gap-8 flex-wrap">
              <span v-for="tag in englishTags" :key="tag" class="tag-chip">{{
                tag
              }}</span>
            </div>
          </div>
        </div>
      </div>
      <CategoryCard
        v-for="child in category.children"
        :key="child.categoryId"
        :category="child"
        :theme-id="themeId"
        :depth="depth + 1"
      />
      <div v-if="depth < MAX_DEPTH" class="flex">
        <pn-button
          :icon="plus_small"
          appearance="light"
          variant="borderless"
          left-icon="true"
          aria-label="Add subcategory"
          @click="showAddSubcategoryModal = true"
        >
          Add subcategory
        </pn-button>
      </div>
      <AddCategoryModal
        :open="showAddSubcategoryModal"
        :parent-category-id="category.categoryId"
        :parent-tags="category.tags"
        :theme-id="themeId"
        title="Add subcategory"
        @on-close="showAddSubcategoryModal = false"
        @on-save="showAddSubcategoryModal = false"
      />
    </div>
    <ConfirmationModal
      :open="showDeleteModal"
      :heading="deleteHeading"
      :showLoader="isDeleting"
      @onCancel="showDeleteModal = false"
      @onConfirm="confirmDelete"
    />
    <AddCategoryModal
      :open="showEditModal"
      :category="category"
      :theme-id="themeId"
      title="Edit category"
      @on-close="showEditModal = false"
      @on-save="showEditModal = false"
    />
  </div>
</template>

<style scoped>
.category-card {
  width: 100%;
  border-radius: 16px;
  background: #ffffff;
  box-shadow:
    0 0.3px 0.9px rgba(0, 0, 0, 0.12),
    0 1.6px 3.6px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
}
.category-header {
  min-height: 56px;
  padding: 4px 8px 4px 4px;
}
.flex-spacer {
  flex: 1;
}
.expanded-content {
  padding: 0 16px 16px 48px;
}
.category-english-name {
  color: #666666;
}
.tags-wrapper {
  background: #f8f8f8;
  border-radius: 8px;
  padding: 12px 16px;
}
.tags-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.tag-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 100px;
  background: #e8f0fe;
  font-size: 14px;
  white-space: nowrap;
}
</style>
